import { useEffect, useLayoutEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  apiDeleteRequest,
  apiGetRequest,
  apiPostRequest,
} from "../../../../Helpers/Requests";
import endpoints from "../../../../Helpers/Endpoints";
import { useSnackbar } from "../../../../Components/SnackBar";
import NewPricelistCreatedModal from "../../../../Components/Modals/NewPricelistCreatedModal";
import PricelistModal from "../../Modals/PricelistModal";
import DeleteDeactivatePricelistDialog from "../../../../Components/Modals/DeleteDeactivatePricelistDialog/DeleteDeactivatePricelistDialog";
import {
  communityBtnBoxStyle,
  communityBtnStyle,
  containerStyle,
  feesPricingButtonStyle,
  scrollbarContainerStyle,
} from "../../styles/styles";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AllPricelistsSection from "./AllPricelistsSection";
import MyPricelistsSection from "./MyPricelistsSection";
import FilterTabs from "./FilterTabs";
import { sortByDisplayOrder } from "../../../../Utils/SortData";
import ProductsContent from "../ProductsContent";

const PricelistsContent = ({
  setPricelistsLoader,
  handleCommunityManagerClick,
}) => {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("services");
  const [openPricelistModal, setOpenPricelistModal] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categories, setCategories] = useState([]);
  const [pricelists, setPricelists] = useState([]);
  const [deletePricelistLoader, setDeletePricelistLoader] = useState(false);
  const [openNewPricelistCreatedModal, setOpenNewPricelistCreatedModal] =
    useState(false);
  const [pricelistsLength, setPricelistsLength] = useState(0);
  const [loader, setLoader] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [allPricelistsData, setAllPricelistsData] = useState([]);
  const [allPricelistsLoader, setAllPricelistsLoader] = useState(false);

  const { t } = useTranslation([
    "pricelist-search",
    "categories",
    "pricelist-header-content",
  ]);

  const handleRemoveClick = (pricelist) => {
    setSelectedForDelete(pricelist);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (selectedForDelete) {
      try {
        setDeletePricelistLoader(true);
        const response = await apiDeleteRequest(
          endpoints.deletePricelist(selectedForDelete.subcategory_id)
        );
        if (response.status === 200) {
          snackbar(
            `${t(selectedForDelete.subcategory_name, { ns: "categories" })} ${t(
              "delete_pricelist_message"
            )}`,
            "success"
          );
          getPricelist();
          getAllPricelistsData();
          handleCloseDeleteModal();
        }
      } catch (ex) {
        console.log("exception occured while deleting pricelist: ", ex);
      } finally {
        setDeletePricelistLoader(false);
      }
    }
  };

  const handleManageClick = async (sub_category_id) => {
    try {
      setPricelistsLoader(true);
      const response = await apiGetRequest(
        endpoints.getPricelistDetails(sub_category_id)
      );
      console.log("response:", response);

      if (response.status === 200) {
        console.log("category:", response.data);
        setSelectedForEdit(response.data.pricelist);
        setOpenPricelistModal(true);
      }
    } catch (ex) {
      console.log("exception occured while getting stats ", ex);
    } finally {
      setPricelistsLoader(false);
    }
  };

  const getPricelist = async () => {
    try {
      setLoader(true);
      const response = await apiGetRequest(endpoints.supplierPricelists);
      if (response.status === 200) {
        const pricelistsData = response.data.pricelists;
        const sortedResults = sortByDisplayOrder(pricelistsData);
        setPricelists(sortedResults);
        setPricelistsLength(pricelistsData.length);
      }
    } catch (ex) {
      console.log("exception occured while getting stats ", ex);
    } finally {
      setLoader(false);
    }
  };

  useLayoutEffect(() => {
    getPricelist();
  }, []);

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedForDelete(null);
  };

  const getCategories = async () => {
    try {
      const response = await apiGetRequest(endpoints.getPricingCategories);
      if (response.status === 200) {
        const sortedResults = sortByDisplayOrder(response.data.categories);
        setCategories(sortedResults);
      } else {
        console.log(
          "Categories data is missing or not in expected format:",
          response.data
        );
        setCategories([]);
      }
    } catch (error) {
      console.log("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    getCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewPricelistModalAction = () => {
    setOpenNewPricelistCreatedModal(false);
    navigate("/find-communities");
  };

  const getAllPricelistsData = async () => {
    setAllPricelistsLoader(true);

    try {
      const url = !searchTerm.trim()
        ? endpoints.searchPricelist
        : `${endpoints.searchPricelist}?searchTerm=${searchTerm}`;

      const response = await apiGetRequest(url);

      if (response.status === 200) {
        const sortedResults = sortByDisplayOrder(response.data.results);

        setAllPricelistsData(sortedResults);
      }
    } catch (error) {
      console.error("Error fetching initial pricelists:", error);
    } finally {
      setAllPricelistsLoader(false);
    }
  };

  const handleRequestPricelist = async () => {
    try {
      const requestBody = {
        category_name: searchTerm,
      };
      await apiPostRequest(endpoints.missingCategoryRequest, requestBody);

      snackbar(t("request_sent_alert", { searchTerm }), "success");
    } catch (error) {
      console.error("Error requesting pricelist:", error);
    }
  };

  return (
    <Box sx={containerStyle}>
      <Box sx={communityBtnBoxStyle}>
        <Box onClick={handleCommunityManagerClick} sx={communityBtnStyle}>
          <img src="/glass_icon.svg" alt="glass_icon" width={24} />
          <Typography
            sx={{
              color: "#2F2B3D",
              fontSize: "15px",
              fontWeight: 500,
              lineHeight: "100%",
            }}
          >
            {t("view_as_community_manager")}
          </Typography>
        </Box>

        <Typography
          as="span"
          onClick={() =>
            window.open(
              "https://communit-giveback-network.lovable.app/",
              "_blank"
            )
          }
          sx={feesPricingButtonStyle}
        >
          {t("pricelist-header-content:fees_pricing")}
          <img src="/icons/external-link-icon.svg" alt="external-link-icon" />
        </Typography>
      </Box>

      <FilterTabs activeTab={activeTab} setActiveTab={setActiveTab} />

      {activeTab === "services" && (
      <Box className="custom-scrollbar" sx={scrollbarContainerStyle}>
        <MyPricelistsSection
          {...{
            pricelists,
            pricelistsLength,
            handleManageClick,
            handleRemoveClick,
            loader,
          }}
        />
        <AllPricelistsSection
          {...{
            handleManageClick,
            getAllPricelistsData,
            searchTerm,
            setSearchTerm,
            allPricelistsData,
            setAllPricelistsData,
            allPricelistsLoader,
            handleRequestPricelist,
          }}
        />
      </Box>
      )}

      {activeTab === "products" && (
        <ProductsContent vertical="products" />
      )}

      {/* Restaurant menus reuse the catalog manager — the BE exposes the same
          endpoints under /api/restaurants, so only the vertical differs. */}
      {activeTab === "food" && (
        <ProductsContent vertical="restaurants" />
      )}

      {showDeleteModal && selectedForDelete && (
        <DeleteDeactivatePricelistDialog
          open={showDeleteModal}
          onClose={handleCloseDeleteModal}
          onConfirm={confirmDelete}
          pricelist={selectedForDelete}
          actionLoader={deletePricelistLoader}
          dialogTitle={t("delete_dialog.title")}
          dialogHeading={`${t("delete_dialog.are_you_sure_part_1")} ${t(
            selectedForDelete?.subcategory_name,
            { ns: "categories" }
          )} ${t("delete_dialog.are_you_sure_part_2")}`}
          dialogDescription={t("delete_dialog.services_stop")}
          confirmBtnText={t("delete_dialog.button_delete_pricelist")}
        />
      )}

      {openPricelistModal && selectedForEdit && (
        <PricelistModal
          open={openPricelistModal}
          onClose={() => {
            setOpenPricelistModal(false);
            setSelectedForEdit(null);
          }}
          onNext={() => console.log("Next clicked")}
          pricelist={selectedForEdit}
          categories={categories}
          getPricelist={getPricelist}
          getAllPricelistsData={getAllPricelistsData}
          setOpenNewPricelistCreatedModal={setOpenNewPricelistCreatedModal}
          pricelistsLength={pricelistsLength}
          pricelists={pricelists}
        />
      )}

      <NewPricelistCreatedModal
        {...{
          open: openNewPricelistCreatedModal,
          onClose: () => setOpenNewPricelistCreatedModal(false),
          onConfirm: handleNewPricelistModalAction,
        }}
      />
    </Box>
  );
};

export default PricelistsContent;
